import { Link } from "@tanstack/react-router";
import { FormEvent, useEffect, useState } from "react";

import { RequireAuth } from "../components/RequireAuth";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";

interface List {
  id: string;
  name: string;
  description: string | null;
  visibility: "private" | "public";
  slug: string;
  created_at: string;
  updated_at: string;
}

interface ListWithFeedStatus extends List {
  inFeed?: boolean;
}

// Input length limits for security
const MAX_LIST_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;

export default function ListsPage() {
  const { user } = useAuth();
  const [lists, setLists] = useState<ListWithFeedStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [feedLoading, setFeedLoading] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    visibility: "private" as "private" | "public",
  });

  // Load user's lists
  useEffect(() => {
    if (!user) return;

    const loadLists = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from("lists")
          .select("id, name, description, visibility, slug, created_at, updated_at")
          .eq("owner_id", user.id)
          .order("created_at", { ascending: false });

        if (fetchError) {
          if (import.meta.env.DEV) {
            console.error("Error loading lists:", fetchError);
          }
          setError("Failed to load lists");
          setLoading(false);
          return;
        }

        const listsData = data || [];

        // Check feed status for public lists
        const publicListIds = listsData
          .filter((list) => list.visibility === "public")
          .map((list) => list.id);

        if (publicListIds.length > 0) {
          const { data: feedData } = await supabase
            .from("feed_posts")
            .select("list_id")
            .in("list_id", publicListIds);

          const feedListIds = new Set((feedData || []).map((fp) => fp.list_id));

          const listsWithFeedStatus = listsData.map((list) => ({
            ...list,
            inFeed: list.visibility === "public" ? feedListIds.has(list.id) : false,
          }));

          setLists(listsWithFeedStatus);
        } else {
          setLists(listsData);
        }

        setLoading(false);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error("Unexpected error loading lists:", err);
        }
        setError("An unexpected error occurred");
        setLoading(false);
      }
    };

    loadLists();
  }, [user]);

  // Handle create list
  const handleCreateList = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const trimmedName = formData.name.trim();
    const trimmedDescription = formData.description.trim();

    if (!trimmedName) {
      setError("List name is required");
      return;
    }

    if (trimmedName.length > MAX_LIST_NAME_LENGTH) {
      setError(`List name must be ${MAX_LIST_NAME_LENGTH} characters or less`);
      return;
    }

    if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
      setError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const { data, error: createError } = await supabase
        .from("lists")
        .insert([
          {
            owner_id: user.id,
            name: trimmedName.slice(0, MAX_LIST_NAME_LENGTH),
            description: trimmedDescription ? trimmedDescription.slice(0, MAX_DESCRIPTION_LENGTH) : null,
            visibility: formData.visibility,
            // slug will be generated by DB trigger
          },
        ])
        .select()
        .single();

      if (createError) {
        if (import.meta.env.DEV) {
          console.error("Error creating list:", createError);
        }
        setError("Failed to create list. Please try again.");
        setCreating(false);
        return;
      }

      // PostHog: Track list creation
      try {
        import("posthog-js").then(({ default: posthog }) => {
          posthog.capture("list_created", {
            list_id: data.id,
            visibility: data.visibility,
          });
        });
      } catch (e) {
        // Silently ignore PostHog errors
      }

      // Add new list to state
      setLists([data, ...lists]);
      setFormData({ name: "", description: "", visibility: "private" });
      setShowCreateForm(false);
      setCreating(false);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Unexpected error creating list:", err);
      }
      setError("An unexpected error occurred");
      setCreating(false);
    }
  };

  // Handle delete list
  const handleDeleteList = async (listId: string) => {
    if (!user) return;
    if (!confirm("Are you sure you want to delete this list? This action cannot be undone.")) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from("lists")
        .delete()
        .eq("id", listId)
        .eq("owner_id", user.id);

      if (deleteError) {
        if (import.meta.env.DEV) {
          console.error("Error deleting list:", deleteError);
        }
        setError("Failed to delete list");
        return;
      }

      // Remove from state
      setLists(lists.filter((list) => list.id !== listId));
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Unexpected error deleting list:", err);
      }
      setError("An unexpected error occurred");
    }
  };

  // Handle add to feed
  const handleAddToFeed = async (listId: string) => {
    if (!user) return;

    setFeedLoading({ ...feedLoading, [listId]: true });
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from("feed_posts")
        .insert([{ list_id: listId }]);

      if (insertError) {
        if (import.meta.env.DEV) {
          console.error("Error adding to feed:", insertError);
        }
        setError("Failed to add to feed. Please try again.");
        setFeedLoading({ ...feedLoading, [listId]: false });
        return;
      }

      // Update list state
      setLists(
        lists.map((list) => (list.id === listId ? { ...list, inFeed: true } : list))
      );
      setFeedLoading({ ...feedLoading, [listId]: false });

      // PostHog: Track feed add
      try {
        import("posthog-js").then(({ default: posthog }) => {
          posthog.capture("list_added_to_feed", {
            list_id: listId,
          });
        });
      } catch (e) {
        // Silently ignore PostHog errors
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Unexpected error adding to feed:", err);
      }
      setError("An unexpected error occurred");
      setFeedLoading({ ...feedLoading, [listId]: false });
    }
  };

  // Handle remove from feed
  const handleRemoveFromFeed = async (listId: string) => {
    if (!user) return;

    setFeedLoading({ ...feedLoading, [listId]: true });
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from("feed_posts")
        .delete()
        .eq("list_id", listId);

      if (deleteError) {
        if (import.meta.env.DEV) {
          console.error("Error removing from feed:", deleteError);
        }
        setError("Failed to remove from feed. Please try again.");
        setFeedLoading({ ...feedLoading, [listId]: false });
        return;
      }

      // Update list state
      setLists(
        lists.map((list) => (list.id === listId ? { ...list, inFeed: false } : list))
      );
      setFeedLoading({ ...feedLoading, [listId]: false });

      // PostHog: Track feed remove
      try {
        import("posthog-js").then(({ default: posthog }) => {
          posthog.capture("list_removed_from_feed", {
            list_id: listId,
          });
        });
      } catch (e) {
        // Silently ignore PostHog errors
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Unexpected error removing from feed:", err);
      }
      setError("An unexpected error occurred");
      setFeedLoading({ ...feedLoading, [listId]: false });
    }
  };

  return (
    <RequireAuth>
      <div className="mx-auto flex w-full max-w-6xl flex-col px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="mb-3 text-3xl font-bold tracking-tight text-accent sm:mb-2 sm:text-4xl md:text-5xl">
            My Lists
          </h1>
          <p className="text-base text-text/70 sm:text-sm">
            Create and manage your food lists to share with others.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Create List Button/Form */}
        {!showCreateForm ? (
          <button
            onClick={() => setShowCreateForm(true)}
            className="mb-6 rounded-lg border border-accent/60 bg-accent/15 px-6 py-3 text-base font-medium text-accent transition-colors hover:border-accent hover:bg-accent/20 sm:mb-8 sm:py-2 sm:text-sm"
          >
            Create New List
          </button>
        ) : (
          <form onSubmit={handleCreateList} className="mb-6 rounded-lg border border-surface/60 bg-surface/30 p-6 sm:mb-8">
            <h2 className="mb-4 text-xl font-semibold text-text">Create New List</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-text/80">
                  List Name *
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-lg border border-surface/60 bg-bg px-4 py-2 text-text focus:border-accent focus:outline-none"
                  placeholder="e.g., Date Night Spots"
                  required
                  maxLength={MAX_LIST_NAME_LENGTH}
                />
                {formData.name.length > MAX_LIST_NAME_LENGTH * 0.9 && (
                  <p className="mt-1 text-xs text-text/50">
                    {formData.name.length}/{MAX_LIST_NAME_LENGTH} characters
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="description" className="mb-2 block text-sm font-medium text-text/80">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full rounded-lg border border-surface/60 bg-bg px-4 py-2 text-text focus:border-accent focus:outline-none"
                  placeholder="Optional description..."
                  rows={3}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                />
                {formData.description.length > MAX_DESCRIPTION_LENGTH * 0.9 && (
                  <p className="mt-1 text-xs text-text/50">
                    {formData.description.length}/{MAX_DESCRIPTION_LENGTH} characters
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="visibility" className="mb-2 block text-sm font-medium text-text/80">
                  Visibility
                </label>
                <select
                  id="visibility"
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value as "private" | "public" })}
                  className="w-full rounded-lg border border-surface/60 bg-bg px-4 py-2 text-text focus:border-accent focus:outline-none"
                >
                  <option value="private">Private (only you can see)</option>
                  <option value="public">Public (shareable link)</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-lg border border-accent/60 bg-accent/15 px-6 py-2 text-sm font-medium text-accent transition-colors hover:border-accent hover:bg-accent/20 disabled:opacity-50"
                >
                  {creating ? "Creating..." : "Create List"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setFormData({ name: "", description: "", visibility: "private" });
                  }}
                  className="rounded-lg border border-surface/60 bg-surface/30 px-6 py-2 text-sm font-medium text-text transition-colors hover:bg-surface/50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Lists Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent/30 border-t-accent"></div>
          </div>
        ) : lists.length === 0 ? (
          <div className="rounded-lg border border-surface/60 bg-surface/30 p-8 text-center">
            <p className="text-text/60">You haven't created any lists yet.</p>
            <p className="mt-2 text-sm text-text/50">Create your first list to get started!</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-surface/60 bg-surface/30">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface/60">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text">Visibility</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text">Feed</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text">Created</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-text">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lists.map((list) => (
                  <tr key={list.id} className="border-b border-surface/60 last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-text">{list.name}</div>
                      {list.description && (
                        <div className="mt-1 hidden text-sm text-text/60 md:block">{list.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${
                          list.visibility === "public"
                            ? "bg-accent/20 text-accent"
                            : "bg-surface/60 text-text/60"
                        }`}
                      >
                        {list.visibility === "public" ? "Public" : "Private"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {list.visibility === "public" ? (
                        <span
                          className={`inline-block whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${
                            list.inFeed
                              ? "bg-accent/20 text-accent"
                              : "bg-surface/60 text-text/60"
                          }`}
                        >
                          {list.inFeed ? "In Feed" : "Not in Feed"}
                        </span>
                      ) : (
                        <span className="text-xs text-text/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text/60">
                      {new Date(list.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to="/lists/$listId"
                          params={{ listId: list.id }}
                          className="rounded border border-surface/60 bg-surface/30 px-2 py-1 text-xs font-medium text-text transition-colors hover:border-accent/60 hover:bg-surface/50"
                        >
                          Manage
                        </Link>
                        {list.visibility === "public" && (
                          <>
                            <button
                              onClick={async () => {
                                const url = `${window.location.origin}/l/${list.slug}`;
                                try {
                                  await navigator.clipboard.writeText(url);
                                  alert("Public link copied to clipboard!");
                                  
                                  // PostHog: Track share click
                                  try {
                                    import("posthog-js").then(({ default: posthog }) => {
                                      posthog.capture("list_share_clicked", {
                                        list_id: list.id,
                                        slug: list.slug,
                                        visibility: list.visibility,
                                      });
                                    });
                                  } catch (e) {
                                    // Silently ignore PostHog errors
                                  }
                                } catch (err) {
                                  if (import.meta.env.DEV) {
                                    console.error("Failed to copy link:", err);
                                  }
                                }
                              }}
                              className="rounded border border-accent/60 bg-accent/15 px-2 py-1 text-xs font-medium text-accent transition-colors hover:border-accent hover:bg-accent/20"
                            >
                              Copy Link
                            </button>
                            <button
                              onClick={() =>
                                list.inFeed
                                  ? handleRemoveFromFeed(list.id)
                                  : handleAddToFeed(list.id)
                              }
                              disabled={feedLoading[list.id]}
                              className="rounded border border-accent/60 bg-accent/15 px-2 py-1 text-xs font-medium text-accent transition-colors hover:border-accent hover:bg-accent/20 disabled:opacity-50"
                            >
                              {feedLoading[list.id]
                                ? "..."
                                : list.inFeed
                                  ? "Remove from Feed"
                                  : "Add to Feed"}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteList(list.id)}
                          className="rounded border border-red-500/30 bg-red-500/10 px-2 py-1 text-xs font-medium text-red-400 transition-colors hover:border-red-500 hover:bg-red-500/20"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </RequireAuth>
  );
}
